import React, { lazy, Suspense, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Appointment as AppointmentResponse,
  AppointmentUpdate,
  useDurationSchedule,
  useRecurrence,
  useFormsAndReminders,
  useDirtyStateTracking,
  useAppointmentFormValidation,
  useAppointmentSubmission,
  useAppointmentFormReset,
  useTelehealth,
  AppointmentFormFields,
  CustomRecurrenceModal,
  DirtyChangesAlert,
  PRESET_DURATIONS,
  NoShowAction,
  PatientConfirmedStatus,
} from '@features/appointments';
import { useTemplatesList } from '@features/user';
import { Patient } from '../../../../types/types';
import { DeleteConfirmDialog } from '../../../../components/DeleteConfirmDialog';
import { format, add } from 'date-fns';
import { useAppointmentTypes, AppointmentTypeModal } from '@features/appointment-types';
import { useEventTracking } from '@services/statsig';
import {
  EligibilityCheckCard,
  useEligibilityCardState,
  apiToSnapshot,
  type EligibilityCheckResponse,
  type EligibilitySnapshot,
} from '@features/patient-coverages';
import { EligibilityInfoPanel } from './EligibilityInfoPanel';

function AppointmentEligibilitySection({
  patientId,
  encounterId,
}: {
  patientId?: string;
  encounterId?: string;
}) {
  const { state, eligibilityData } = useEligibilityCardState({ patientId, encounterId });

  if (state === 'check_complete' && eligibilityData) {
    return (
      <Box sx={{ mt: 2 }}>
        <EligibilityInfoPanel
          eligibility={apiToSnapshot(eligibilityData)}
          patientId={patientId}
          encounterId={encounterId}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <EligibilityCheckCard patientId={patientId} encounterId={encounterId} />
    </Box>
  );
}

interface ContentProps {
  appointment: AppointmentResponse;
  onAppointmentUpdate: (
    appointmentId: string,
    updates: AppointmentUpdate,
    seriesId?: string,
  ) => Promise<void>;
  onAppointmentDelete?: (appointmentId: string, seriesId?: string) => Promise<void>;
  handleRecordClick: () => Promise<void>;
}

export const Content: React.FC<ContentProps> = ({
  appointment,
  onAppointmentUpdate,
  onAppointmentDelete,
  handleRecordClick,
}) => {
  const templatesList = useTemplatesList();
  const { appointment: appointmentTracking } = useEventTracking();

  // Fetch all appointment types to find the matching one
  const { appointmentTypes, isLoading: isAppointmentTypesLoading } = useAppointmentTypes();

  // Patient selection state
  const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>(undefined);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // State for new appointment type modal
  const [showNewAppointmentTypeModal, setShowNewAppointmentTypeModal] = useState(false);
  const [newlyCreatedTypeId, setNewlyCreatedTypeId] = useState<string | null>(null);

  // Initialize modular hooks
  const durationSchedule = useDurationSchedule();

  const recurrence = useRecurrence();

  const telehealth = useTelehealth({
    initialTelehealth: appointment.telehealth ?? false,
  });

  // Initialize dirty tracking first without dependencies
  const dirtyTrackingRef = React.useRef<any>(null);

  const formsAndReminders = useFormsAndReminders({
    initialAppointment: appointment,
    // templateId: templatesList?.[0]?.template_id || null,
    onMarkDirty: field => dirtyTrackingRef.current?.markDirty(field),
  });

  const dirtyTracking = useDirtyStateTracking({
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    selectedDuration: durationSchedule.selectedDuration,
    currentTelehealth: telehealth.telehealth,
    currentForms: formsAndReminders.selectedForms,
    currentReminders: formsAndReminders.selectedReminders,
  });

  // Update ref
  dirtyTrackingRef.current = dirtyTracking;

  // Track if initial appointment data has been loaded
  const [hasLoadedAppointmentData, setHasLoadedAppointmentData] = useState(false);

  // Auto-populate defaults from appointment type when not dirty
  useEffect(() => {
    if (!formsAndReminders.selectedAppointmentType) return;

    // Don't auto-populate if we're overriding defaults
    if (appointment.override_defaults) return;

    // Don't auto-populate if we have not loaded appointment data yet
    if (!hasLoadedAppointmentData) return;

    // Auto-populate duration if not dirty and default exists
    if (
      !dirtyTracking.isDirty('duration') &&
      formsAndReminders.selectedAppointmentType.default_duration
    ) {
      durationSchedule.handleDurationSelect(
        formsAndReminders.selectedAppointmentType.default_duration,
      );
    }

    // Auto-populate forms if not dirty
    if (!dirtyTracking.isDirty('forms')) {
      formsAndReminders.populateFormsFromDefaults(formsAndReminders.selectedAppointmentType);
    }

    // Auto-populate reminders if not dirty
    if (!dirtyTracking.isDirty('reminders')) {
      formsAndReminders.populateRemindersFromDefaults(formsAndReminders.selectedAppointmentType);
    }

    // Auto-populate telehealth if not dirty
    if (
      !dirtyTracking.isDirty('telehealth') &&
      formsAndReminders.selectedAppointmentType.telehealth !== undefined
    ) {
      telehealth.setTelehealth(formsAndReminders.selectedAppointmentType.telehealth ?? false);
    }
  }, [
    formsAndReminders.selectedAppointmentType,
    hasLoadedAppointmentData,
    appointment.override_defaults,
  ]);

  // Form validation
  const validation = useAppointmentFormValidation({
    selectedPatient,
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    durationSchedule,
    mode: 'edit',
  });

  // Form submission
  const submission = useAppointmentSubmission({
    mode: 'edit',
    selectedPatient,
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    durationSchedule,
    recurrence,
    templateId: templatesList?.[0]?.template_id || null,
    selectedForms: formsAndReminders.selectedForms,
    selectedReminders: formsAndReminders.selectedReminders,
    telehealth: telehealth.telehealth,
    hasDirtyChanges: dirtyTracking.showDirtyAlert && !appointment.override_defaults,
    appointment,
    onAppointmentUpdate,
  });

  // Form reset
  const { resetForm: resetFormData } = useAppointmentFormReset({
    durationSchedule,
    recurrence,
    dirtyTracking,
    onPatientReset: () => setSelectedPatient(undefined),
  });

  // Coordinate between hooks - duration changes mark as dirty
  const handleDurationChange = (duration: number) => {
    durationSchedule.handleDurationSelect(duration);
    dirtyTracking.markDirty('duration');
  };

  // Coordinate between hooks - telehealth changes mark as dirty
  const handleTelehealthChange = (checked: boolean) => {
    telehealth.handleTelehealthChange(checked);
    dirtyTracking.markDirty('telehealth');
  };

  // Handler for patient selection
  const handlePatientSelect = (patient: Patient | undefined) => {
    setSelectedPatient(patient);
  };

  const handleSaveAppointment = async () => {
    if (!validation.isFormValid) {
      submission.setError(validation.getValidationMessage());
      return;
    }

    await submission.submit();
  };

  const handleDeleteAppointment = async () => {
    if (!appointment?.appointment_id) return;

    setIsDeleting(true);
    try {
      // Check if this is a recurring appointment
      if (appointment.series && onAppointmentDelete) {
        // Use the provided handler for recurring appointments
        await onAppointmentDelete(appointment.appointment_id, appointment.series.series_id);
      } else if (onAppointmentDelete) {
        // Single appointment deletion
        await onAppointmentDelete(appointment.appointment_id, undefined);
      } else {
        // Fallback if no handler provided (shouldn't happen)
        console.error('No onAppointmentDelete handler provided');
        submission.setError('Unable to delete appointment. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting appointment:', err);
      submission.setError('Failed to delete appointment. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle dirty alert actions
  const handleUpdateAppointmentType = async () => {
    try {
      await formsAndReminders.handleUpdateAppointmentType(
        durationSchedule.selectedDuration,
        telehealth.telehealth,
      );
      dirtyTracking.resetDirtyState();
    } catch (error) {
      console.error('Failed to update appointment type:', error);
    }
  };

  // Initialize appointment data when component mounts or appointment changes
  useEffect(() => {
    if (!appointment) return;
    submission.setError(null);

    // Find and set the appointment type
    if (appointment.encounter_type_id && appointmentTypes) {
      // If we have a newly created type, use that instead
      if (newlyCreatedTypeId) {
        const newType = appointmentTypes.find(
          type => type.encounter_type_id === newlyCreatedTypeId,
        );
        if (
          newType &&
          formsAndReminders.selectedAppointmentType?.encounter_type_id !== newlyCreatedTypeId
        ) {
          // Don't re-select if already selected
          return;
        }
        setNewlyCreatedTypeId(null); // Clear after using
      } else {
        const matchingType = appointmentTypes.find(
          type => type.encounter_type_id === appointment.encounter_type_id,
        );
        if (matchingType) {
          formsAndReminders.handleAppointmentTypeSelect(matchingType);
        }
      }
    }

    // Populate forms and reminders from appointment data
    formsAndReminders.populateFormsFromAppointment(appointment);
    formsAndReminders.populateRemindersFromAppointment(appointment);

    if (appointment.start_time) {
      // Set initial date and time values
      const scheduledDate = new Date(appointment.start_time);
      durationSchedule.setDate(format(scheduledDate, 'yyyy-MM-dd'));
      durationSchedule.setStartTime(format(scheduledDate, 'HH:mm'));

      // Set duration
      if (appointment.duration) {
        durationSchedule.setSelectedDuration(appointment.duration);

        // Check if it's a preset duration
        const isPresetDuration = PRESET_DURATIONS.some(
          preset => preset.minutes === appointment.duration,
        );

        durationSchedule.setIsCustomDuration(!isPresetDuration);

        // Calculate end time based on duration
        const endDateTime = add(scheduledDate, {
          minutes: appointment.duration,
        });
        durationSchedule.setEndTime(format(endDateTime, 'HH:mm'));
      } else {
        // Default duration if not set
        durationSchedule.setSelectedDuration(30);
        durationSchedule.setIsCustomDuration(false);

        // Calculate default end time
        const endDateTime = add(scheduledDate, { minutes: 30 });
        durationSchedule.setEndTime(format(endDateTime, 'HH:mm'));
      }

      // Parse series.rrule if it exists
      if (appointment.series?.rrule) {
        recurrence.parseRRule(appointment.series.rrule);
      } else {
        recurrence.setRecurrence('none');
      }
    }

    // Set initial patient if available
    if (appointment.patient) {
      // Convert PatientInfo to Patient format
      const patientData: Patient = {
        patient_id: appointment.patient.patient_id,
        first_name: appointment.patient.name?.split(' ')[0] || '',
        last_name: appointment.patient.name?.split(' ').slice(1).join(' ') || '',
        dob: appointment.patient.dob || undefined,
        phone: appointment.patient.telephone || undefined,
        identifier: appointment.patient.identifier || undefined,
      };
      setSelectedPatient(patientData);
    } else {
      setSelectedPatient(undefined);
    }

    // Set telehealth from appointment data if available
    if (appointment.telehealth !== undefined && appointment.telehealth !== null) {
      telehealth.setTelehealth(appointment.telehealth);
    }

    // Mark that we've loaded the appointment data
    setHasLoadedAppointmentData(true);

    return () => {
      // Reset component state when unmounting
      resetFormData();
      setHasLoadedAppointmentData(false);
    };
  }, [appointment?.appointment_id]); // Only re-run when appointment ID changes, not when appointmentTypes updates

  if (!appointment || isAppointmentTypesLoading) {
    return null;
  }

  // Error state
  if (submission.error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {submission.error}
      </Alert>
    );
  }

  // Default state (appointment exists, no error)
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'scroll',
            pb: 4,
            pt: 1,
          }}
        >
          {/* Suggested Appointment Banner */}
          {appointment.status === 'suggested' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 4,
              }}
            >
              <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Suggested Appointment
              </Typography>
            </Box>
          )}

          <AppointmentFormFields
            // Patient
            selectedPatient={selectedPatient}
            onPatientSelect={handlePatientSelect}
            // Appointment Type
            selectedAppointmentType={formsAndReminders.selectedAppointmentType}
            onAppointmentTypeSelect={formsAndReminders.handleAppointmentTypeSelect}
            // Duration Schedule
            date={durationSchedule.date}
            startTime={durationSchedule.startTime}
            endTime={durationSchedule.endTime}
            selectedDuration={durationSchedule.selectedDuration}
            isCustomDuration={durationSchedule.isCustomDuration}
            onDateChange={durationSchedule.handleDateChange}
            onStartTimeChange={durationSchedule.handleStartTimeChange}
            onEndTimeChange={durationSchedule.handleEndTimeChange}
            onDurationSelect={handleDurationChange}
            onCustomDurationSelect={durationSchedule.handleCustomDuration}
            // Recurrence
            recurrence={recurrence.recurrence}
            customRecurrenceRule={recurrence.customRecurrenceRule}
            onRecurrenceChange={recurrence.handleRecurrenceChange}
            onCustomRecurrenceClick={() => recurrence.setIsCustomRecurrenceModalOpen(true)}
            getRecurrenceText={recurrence.getRecurrenceText}
            // Telehealth
            telehealth={telehealth.telehealth}
            onTelehealthChange={handleTelehealthChange}
            // Forms and Reminders
            selectedForms={formsAndReminders.selectedForms}
            selectedReminders={formsAndReminders.selectedReminders}
            onAddForm={formsAndReminders.handleAddForm}
            onUpdateForm={formsAndReminders.handleUpdateForm}
            onRemoveForm={formsAndReminders.handleRemoveForm}
            onAddReminder={formsAndReminders.handleAddReminder}
            onUpdateReminder={formsAndReminders.handleUpdateReminder}
            onRemoveReminder={formsAndReminders.handleRemoveReminder}
            // Mode
            mode="view"
            showRecurrence={!appointment.series}
            // Action links
            actionLinks={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PatientConfirmedStatus appointment={appointment} />
                <NoShowAction
                  appointment={appointment}
                  onMarkNoShowClick={() => {
                    appointmentTracking.actionClicked({
                      appointment_id: appointment.appointment_id,
                      action: 'mark_no_show',
                      source: 'appointment_edit',
                    });
                  }}
                />
              </Box>
            }
          />

          {/* Eligibility Info */}
            <AppointmentEligibilitySection
              patientId={appointment.patient?.patient_id}
              encounterId={appointment.encounter?.encounter_id}
            />
        </Box>
        <Box
          sx={{
            mx: -2,
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            bottom: 0,
            background: 'background.paper',
            zIndex: 1,
          }}
        >
          <DirtyChangesAlert
            show={dirtyTracking.showDirtyAlert && !appointment.override_defaults}
            appointmentTypeName={formsAndReminders.selectedAppointmentType?.encounter_type || ''}
            changes={dirtyTracking.changes}
            selectedAppointmentType={formsAndReminders.selectedAppointmentType}
            currentDuration={durationSchedule.selectedDuration}
            currentTelehealth={telehealth.telehealth}
            currentForms={formsAndReminders.selectedForms}
            currentReminders={formsAndReminders.selectedReminders}
            currentTemplate={
              templatesList?.find(
                t => t.template_id === formsAndReminders.selectedAppointmentType?.template_id,
              ) || null
            }
            onUpdateAppointmentType={handleUpdateAppointmentType}
            onCreateNew={() => setShowNewAppointmentTypeModal(true)}
          />

          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Button
              variant="contained"
              onClick={() => {
                // Track the record session action
                appointmentTracking.actionClicked({
                  appointment_id: appointment.appointment_id,
                  action: 'record_session',
                  source: 'appointment_edit',
                });
                handleRecordClick();
              }}
              color="secondary"
              fullWidth
              sx={{
                maxWidth: 300,
              }}
            >
              {appointment?.encounter?.status === 'completed' ||
              appointment?.encounter?.status === 'processing'
                ? 'Go to Note'
                : 'Start Recording'}
            </Button>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                onClick={() => setIsDeleteDialogOpen(true)}
                disabled={submission.isSubmitting || isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
              <Tooltip
                title={!validation.isFormValid ? validation.getValidationMessage() : ''}
                placement="top"
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveAppointment}
                    disabled={submission.isSubmitting || !validation.isFormValid || isDeleting}
                  >
                    {submission.isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Save'
                    )}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      </Box>
      <DeleteConfirmDialog
        open={isDeleteDialogOpen}
        type="appointment"
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          setIsDeleteDialogOpen(false);
          handleDeleteAppointment();
        }}
      />

      <CustomRecurrenceModal
        open={recurrence.isCustomRecurrenceModalOpen}
        onClose={() => recurrence.setIsCustomRecurrenceModalOpen(false)}
        onSave={recurrence.handleCustomRecurrenceSave}
        initialRule={recurrence.customRecurrenceRule || undefined}
      />

      <AppointmentTypeModal
        type="create"
        open={showNewAppointmentTypeModal}
        onClose={() => setShowNewAppointmentTypeModal(false)}
        onSave={newType => {
          // Store the newly created type ID
          setNewlyCreatedTypeId(newType.encounter_type_id);

          // Select the new appointment type
          formsAndReminders.handleAppointmentTypeSelect(newType);

          // Reset dirty state since we're now using the new type
          dirtyTracking.resetDirtyState();

          // Close the modal
          setShowNewAppointmentTypeModal(false);
        }}
        initialName={formsAndReminders.selectedAppointmentType?.encounter_type + ' (Copy)' || ''}
        defaultValues={{
          basedOnAppointmentType: formsAndReminders.selectedAppointmentType,
          overrides: {
            duration: durationSchedule.selectedDuration,
            telehealth: telehealth.telehealth,
            forms: formsAndReminders.selectedForms,
            reminders: formsAndReminders.selectedReminders,
          },
        }}
      />
    </>
  );
};
